import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { getEventById, getEventSlots, resetEvent } from "../../store/events";
import EventShell from "./EventShell";
import useNoIndex from "../../hooks/useNoIndex";
import ClassicBooking from "./ClassicBooking";
import CustomRegister from "./CustomRegister";
import ErrorState from "./ErrorState";
import InactivePage from "./InactivePage";
import Confirmation from "./Confirmation";
import "./events.css";

/**
 * EventApp — route container for the public NSD events flow.
 *
 * The event resolve is blocking: its `pageMode` (classic | custom | inactive)
 * decides which screen renders. Slot availability is a separate call keyed only
 * on the event id, so it runs in parallel and gets a section-level loading state
 * inside the booking screen rather than holding the whole page.
 */

const LoadingState = () => (
  <div className="ev-wrap" style={{ textAlign: "center" }}>
    Loading event…
  </div>
);

/* ── Container ─────────────────────────────────────────────────────────────── */
const EventApp = () => {
  const { eventId } = useParams();
  const dispatch = useDispatch();
  const { event, status, confirmation } = useSelector((state) => state.events);

  useNoIndex();

  useEffect(() => {
    dispatch(resetEvent());
    // Both calls key only on the event id, so they run in parallel.
    dispatch(getEventById(eventId));
    dispatch(getEventSlots(eventId));
  }, [eventId, dispatch]);

  const renderBody = () => {
    if (status === "error") return <ErrorState />;
    if (status !== "ready" || !event) return <LoadingState />;
    if (confirmation) {
      return <Confirmation data={confirmation} />;
    }
    switch (event.pageMode) {
      case "custom":
        return <CustomRegister />;
      case "inactive":
        return <InactivePage event={event} />;
      case "classic":
      default:
        return <ClassicBooking />;
    }
  };

  return <EventShell>{renderBody()}</EventShell>;
};

export default EventApp;
